import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import Popover from "@material-ui/core/Popover";
import { DropdownChevronIcon } from "./Primitives/icons/DropdownChevronIcon";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import { Editor } from "@tldraw/tldraw";
import ColorPickerPopover from "./ColorPickerPopover";
import { hideSelectionTemporarily, updateShapeMouseDown, updateShapeMouseUp } from "../../utils";
import { normalizeShadow, ShadowV2, shadowsToCssBoxShadow } from "../state/newShapes/shadowUtils";
import { useDragToAdjust } from "../hooks/useDragToAdjust";
import { useDraggableColorPicker } from "./useDraggableColorPicker";

const useStyles = makeStyles({
  paper: {
    borderRadius: "8px !important",
    padding: 0,
    border: "1px solid #E2E2FC",
    boxShadow: "0px 5px 25px rgba(28, 43, 54, 0.2)",
  },
  menuPaper: {
    borderRadius: "4px !important",
    "& .MuiMenuItem-root": {
      position: "relative",
      paddingRight: "32px",
      "&:hover": {
        backgroundColor: "#E2E2FC",
      },
      "&.Mui-selected": {
        backgroundColor: "#FFFFFF !important",
        border: "1px solid #2E3860",
        borderRadius: "4px",
        "&::after": {
          content: '""',
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          width: "12px",
          height: "12px",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path fill-rule='evenodd' clip-rule='evenodd' d='M3.54 4.86L2.7 5.7L5.4 8.4L11.4 2.4L10.56 1.56L5.4 6.72L3.54 4.86ZM10.8 6C10.8 8.64 8.64 10.8 6 10.8C3.36 10.8 1.2 8.64 1.2 6C1.2 3.36 3.36 1.2 6 1.2C6.48 1.2 6.9 1.26 7.32 1.38L8.28 0.42C7.56 0.18 6.78 0 6 0C2.7 0 0 2.7 0 6C0 9.3 2.7 12 6 12C9.3 12 12 9.3 12 6H10.8Z' fill='%2357578E'/></svg>\")",
        },
      },
      "&.Mui-selected:hover": {
        backgroundColor: "#FFFFFF !important",
      },
    },
  },
  menuItem: {
    "&:hover": {
      backgroundColor: "#E2E2FC",
    },
  },
  listPadding: {
    padding: "0px !important",
  },
});

type LegacyShadowType = {
  id: string;
  dx?: number;
  dy?: number;
  shadowBlur?: number;
  x?: number;
  y?: number;
  blur?: number;
  spread?: number;
  color: string;
  opacity?: number;
  isHidden?: boolean;
  version?: number;
  type?: "drop" | "inner";
};

interface ShadowItemProps {
  shadow: LegacyShadowType;
  type: "outShadow" | "inShadow";
  selectedShape: any;
  setSelectedShape: (shape: any) => void;
  editor: Editor;
  handleOpenPopover: (event: React.MouseEvent<HTMLElement>, shadowId: string, type: "outShadow" | "inShadow") => void;
  handleRemoveShadow: (shadowId: string, type: "outShadow" | "inShadow") => void;
  handleShowToggle: (shadowId: string, type: "outShadow" | "inShadow") => void;
  handleSliderChangeShadowBlur: (key: string, value: number, type: "outShadow" | "inShadow") => void;
  position: Record<string, { x: number; y: number }>;
  setPosition: React.Dispatch<React.SetStateAction<Record<string, { x: number; y: number }>>>;
  handleMouseDown: (e: React.MouseEvent, shadowId: string, type: "outShadow" | "inShadow") => void;
  containerRef: React.RefObject<HTMLDivElement>;
  anchorElOutShadow: Record<string, HTMLElement | null>;
  handleClosePopover: (shadowId: string) => void;
}

const PREVIEW_W = 248;
const PREVIEW_H = 180;
const RECT_W = 132;
const RECT_H = 84;
const POPOVER_W = 280;
const POPOVER_GAP = 15;

/**
 * Shadow-editor popover wrapper that keeps the tldraw canvas pannable
 * while the popover is open — same pattern as ColorPickerPopover.
 *
 * The default MUI Popover renders an invisible-but-event-blocking
 * Backdrop over the whole viewport, which intercepts the mousedown
 * that tldraw needs to start a canvas pan. We disable that by setting
 * `pointerEvents: none` on both the Popover root and the Backdrop, and
 * restore `pointerEvents: auto` on the Paper so the picker itself
 * still receives clicks. That combination is what lets canvas pan
 * (and any other tldraw gesture) work through the popover area.
 *
 * Trade-off: with the Backdrop disabled, MUI's built-in clickaway
 * never fires, so we replace it with a document-level mousedown
 * listener that closes the popover when the click lands outside the
 * Paper content AND outside the trigger element. We exclude the
 * trigger (`popupState.anchorEl`) to avoid a close→reopen flicker
 * when the user clicks the trigger while it's already open — let
 * bindTrigger's own toggle logic handle that case.
 *
 * Lives as a separate component (not inline in PopupState's render
 * prop) because hooks can't be called inside callback-render-props.
 */
type ShadowPopoverRenderProps = {
  /** Ref to attach to the popover's content wrapper (the div with
   *  visible padding). Used to scope outside-click detection AND
   *  drag tracking. */
  paperContentRef: React.RefObject<HTMLDivElement>;
  /** Attach to the content wrapper's `onMouseDown`. The hook checks
   *  `e.target === e.currentTarget` so only clicks on the wrapper's
   *  padding/border start a drag — clicks on the picker controls
   *  pass through. */
  handleBorderMouseDown: (e: React.MouseEvent) => void;
  /** True while the user is dragging the popover. Flip the wrapper's
   *  cursor to `grabbing` for feedback. */
  isDragging: boolean;
};

const CanvasPannableShadowPopover: React.FC<{
  popupState: any;
  paperClassName?: string;
  anchorPosition: { top: number; left: number };
  /** Render-prop child. The consumer attaches the ref + mousedown
   *  handler to its own `p-4` wrapper so the visible padding doubles
   *  as the drag handle (same UX as the color pickers). */
  children: (props: ShadowPopoverRenderProps) => React.ReactNode;
}> = ({ popupState, paperClassName, anchorPosition, children }) => {
  const isOpen = !!popupState?.isOpen;
  // Shared color-picker drag hook owns the offset state, the window-
  // scoped mousemove/mouseup listeners, and the outside-click-to-
  // close handler. Reusing it here keeps the shadow popover's drag
  // behaviour byte-for-byte consistent with the color pickers.
  const { paperContentRef, handleBorderMouseDown, isDragging } = useDraggableColorPicker(
    isOpen,
    (popupState?.anchorEl as Element | null) ?? null,
    popupState?.close ?? (() => {})
  );

  return (
    <Popover
      {...bindPopover(popupState)}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      classes={paperClassName ? { paper: paperClassName } : undefined}
      // pointer-events plumbing — see component JSDoc. Lets the canvas
      // receive the mousedown that starts a pan; Paper is re-enabled
      // below so the picker itself stays interactive.
      style={{ pointerEvents: "none" }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      // `hideBackdrop` removes the Backdrop element entirely (more
      // robust than the `pointer-events: none` trick — the prior
      // approach still left a full-viewport div in the DOM that could
      // intercept gestures even with pointer-events disabled). Combined
      // with `disableScrollLock` which stops MUI from setting
      // `overflow: hidden` on <body> + attaching wheel listeners that
      // can absorb touchpad two-finger gestures before they reach
      // tldraw.
      hideBackdrop
      disableScrollLock
      BackdropProps={{ invisible: true, style: { pointerEvents: "none" } }}
      // Drag offset is layered on via the `--cp-drag-x` / `--cp-drag-y`
      // CSS variables that `useDraggableColorPicker` writes directly
      // to this Paper element. Inline transform reads them; React
      // re-renders never clobber the in-flight drag value because the
      // string itself is constant.
      PaperProps={{
        style: {
          pointerEvents: "auto",
          transform: "translate(var(--cp-drag-x, 0px), var(--cp-drag-y, 0px))",
        },
      }}
    >
      {children({ paperContentRef, handleBorderMouseDown, isDragging })}
    </Popover>
  );
};

const computeEffectsAnchor = (): { top: number; left: number } | null => {
  const el = typeof document !== "undefined" ? document.querySelector("[data-effects-section]") : null;
  if (!el) return null;
  const rect = (el as HTMLElement).getBoundingClientRect();
  return {
    top: rect.top,
    left: Math.max(8, rect.left - POPOVER_GAP - POPOVER_W),
  };
};

const NumericField: React.FC<{
  label?: string;
  iconLeft?: React.ReactNode;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}> = ({ label, iconLeft, value, onChange, min, max, step = 1 }) => {
  const valueRef = useRef(value);
  valueRef.current = value;
  const { onMouseDown, dragging } = useDragToAdjust(() => valueRef.current, onChange, { min, max, step });
  const inputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState<string>(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused || dragging) setText(String(value));
  }, [value, focused, dragging]);

  useEffect(() => {
    if (dragging) {
      inputRef.current?.blur();
      window.getSelection?.()?.removeAllRanges?.();
    }
  }, [dragging]);

  const commitText = (raw: string) => {
    if (raw.trim() === "" || raw === "-" || raw === ".") {
      setText(String(value));
      return;
    }
    const n = parseFloat(raw);
    if (isNaN(n)) {
      setText(String(value));
      return;
    }
    let clamped = n;
    if (min !== undefined) clamped = Math.max(min, clamped);
    if (max !== undefined) clamped = Math.min(max, clamped);
    onChange(clamped);
    setText(String(clamped));
  };

  return (
    <div className="flex-1 bg-white relative rounded-[4px]" style={{ border: "1px solid #E2E2FC" }}>
      <div className="flex items-center gap-2 px-2 py-[4px]">
        <span
          onMouseDown={onMouseDown}
          className="flex items-center select-none"
          style={{ cursor: "ew-resize" }}
          title="Drag to adjust"
        >
          {label && <span className="text-[10px] font-bold text-[#2E3860]">{label}</span>}
          {iconLeft}
        </span>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={focused ? text : String(value)}
          onChange={(e) => {
            setText(e.target.value);
            const v = e.target.value;
            if (v === "" || v === "-" || v === ".") return;
            const n = parseFloat(v);
            if (!isNaN(n)) {
              let clamped = n;
              if (min !== undefined) clamped = Math.max(min, clamped);
              if (max !== undefined) clamped = Math.min(max, clamped);
              onChange(clamped);
            }
          }}
          onFocus={() => {
            setFocused(true);
            setText(String(value));
          }}
          onBlur={() => {
            commitText(text);
            setFocused(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commitText(text);
              inputRef.current?.blur();
            } else if (e.key === "Escape") {
              setText(String(value));
              inputRef.current?.blur();
            }
          }}
          className="flex-1 w-full bg-transparent outline-none text-[10px] leading-[16px] text-[#2E3860] no-arrows"
          style={{ minWidth: 0, cursor: "text" }}
        />
      </div>
    </div>
  );
};

const BlurIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="3" fill="#1A2547" />
    <circle cx="8" cy="8" r="6" stroke="#1A2547" strokeOpacity="0.3" strokeDasharray="2 1" />
  </svg>
);

const SpreadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="3" width="10" height="10" stroke="#1A2547" strokeWidth="1" fill="none" />
    <path d="M1 1 L4 4 M15 1 L12 4 M1 15 L4 12 M15 15 L12 12" stroke="#1A2547" strokeLinecap="round" />
  </svg>
);

const ShadowItem: React.FC<ShadowItemProps> = ({
  shadow: rawShadow,
  type,
  selectedShape,
  setSelectedShape,
  editor,
}) => {
  const arrayKey = type === "outShadow" ? "outShadowArray" : "inShadowArray";
  const v2: ShadowV2 = useMemo(
    () => normalizeShadow(rawShadow as any, type === "outShadow" ? "drop" : "inner"),
    [rawShadow, type]
  );

  const classes = useStyles();
  const previewRef = useRef<HTMLDivElement>(null);
  const blurSliderRef = useRef<HTMLDivElement>(null);
  const [isDraggingLight, setIsDraggingLight] = useState(false);
  const [isDraggingBlur, setIsDraggingBlur] = useState(false);

  const writeShadowProps = useCallback(
    (updates: Partial<ShadowV2>, moveTo?: "outShadowArray" | "inShadowArray") => {
      const currentArrayKey = arrayKey;
      const targetArrayKey = moveTo ?? currentArrayKey;
      const merged: ShadowV2 = { ...v2, ...updates };
      // Mirror to legacy v1 fields for tldraw schema compat
      merged.dx = merged.x;
      merged.dy = merged.y;
      merged.shadowBlur = merged.blur;

      const currentArr = { ...(selectedShape.props[currentArrayKey] || {}) };
      const targetArr =
        targetArrayKey === currentArrayKey ? currentArr : { ...(selectedShape.props[targetArrayKey] || {}) };

      if (targetArrayKey !== currentArrayKey) {
        delete currentArr[v2.id];
      }
      targetArr[v2.id] = merged;

      const updatedShape = {
        ...selectedShape,
        props: {
          ...selectedShape.props,
          [currentArrayKey]: currentArr,
          [targetArrayKey]: targetArr,
        },
      };

      editor.updateShape(updatedShape);
      setSelectedShape(updatedShape);
    },
    [arrayKey, v2, selectedShape, setSelectedShape, editor]
  );

  const handleSelectChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const newType = e.target.value as "outShadow" | "inShadow" | "layerBlur";
    if (newType === type) return;

    if (newType === "layerBlur") {
      const updatedArr = { ...(selectedShape.props[arrayKey] || {}) };
      delete updatedArr[v2.id];
      const updatedShape = {
        ...selectedShape,
        props: {
          ...selectedShape.props,
          [arrayKey]: updatedArr,
          isCreatedFilterBlur: true,
        },
      };
      editor.updateShape(updatedShape);
      setSelectedShape(updatedShape);
      return;
    }

    const targetArrayKey = newType === "outShadow" ? "outShadowArray" : "inShadowArray";
    const newType2: "drop" | "inner" = newType === "outShadow" ? "drop" : "inner";
    writeShadowProps(
      {
        type: newType2,
        x: -v2.x,
        y: -v2.y,
        opacity: newType2 === "inner" ? Math.min(40, v2.opacity) : v2.opacity,
      },
      targetArrayKey
    );
  };

  const updateLightFromMouse = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      // Light position relative to the preview's center. Clamp to the
      // white rect (`RECT_W` × `RECT_H`) so the draggable region matches
      // the visible target — anything past the rect edge looks broken
      // (the crosshair leaves the rect) and corresponds to shadow
      // offsets the NumericFields below already let users type
      // directly. No center / axis snap any more: it was producing
      // off-by-a-few-px jumps that felt fighting-the-mouse rather
      // than helpful.
      const lightX = Math.max(-RECT_W / 2, Math.min(RECT_W / 2, (e as MouseEvent).clientX - rect.left - cx));
      const lightY = Math.max(-RECT_H / 2, Math.min(RECT_H / 2, (e as MouseEvent).clientY - rect.top - cy));
      writeShadowProps({ x: Math.round(-lightX), y: Math.round(-lightY) });
    },
    [writeShadowProps]
  );

  useEffect(() => {
    if (!isDraggingLight) return;
    const onMove = (e: MouseEvent) => updateLightFromMouse(e);
    const onUp = () => setIsDraggingLight(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDraggingLight, updateLightFromMouse]);

  useEffect(() => {
    if (!isDraggingBlur) return;
    const onMove = (e: MouseEvent) => {
      if (!previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      const top = rect.top + 16;
      const bottom = rect.bottom - 16;
      const h = bottom - top;
      const rel = Math.max(0, Math.min(h, e.clientY - top));
      const newBlur = Math.round(50 - (rel / h) * 50);
      writeShadowProps({ blur: newBlur });
    };
    const onUp = () => setIsDraggingBlur(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDraggingBlur, writeShadowProps]);

  const isInnerShadowDisabled = type !== "inShadow" && Object.keys(selectedShape.props.inShadowArray || {}).length > 0;
  const isCreatedFilterBlur = selectedShape.props.isCreatedFilterBlur;
  // Inner shadow is unsupported on text (CSS text-shadow has no inset),
  // on icons (customSvg uses CSS drop-shadow), and on QR codes.
  const isQrShape = selectedShape.type === "customImage" && selectedShape.props?.qrCodeLink !== undefined;
  const canHaveInnerShadow = selectedShape.type !== "customText" && selectedShape.type !== "customSvg" && !isQrShape;
  const isInner = type === "inShadow";

  const previewShadowCss = isInner
    ? `inset ${v2.x}px ${v2.y}px ${v2.blur}px ${v2.spread}px ${rgbaFromHex(v2.color, v2.opacity)}`
    : `${v2.x}px ${v2.y}px ${v2.blur}px ${v2.spread}px ${rgbaFromHex(v2.color, v2.opacity)}`;

  const lightLeft = `calc(50% - ${v2.x}px)`;
  const lightTop = `calc(50% - ${v2.y}px)`;

  const triggerThumbnailShadow = `${v2.x === 0 ? 0 : 1}px ${v2.y === 0 ? 2 : v2.y > 0 ? 2 : -2}px 0px 0px rgba(0,0,0,${(
    v2.opacity / 100
  ).toFixed(2)})`;

  return (
    <div key={v2.id} className="relative flex items-center justify-between">
      <PopupState variant="popover" popupId={`${type}-${v2.id}`}>
        {(popupState: any) => (
          <>
            <div className="flex items-center justify-between cursor-pointer w-full">
              <div className="flex items-center">
                <div
                  {...bindTrigger(popupState)}
                  style={{
                    border: "1px solid #2E3860",
                    boxShadow: isInner
                      ? `inset 0 2px 0 0 rgba(0,0,0,${(v2.opacity / 100).toFixed(2)})`
                      : triggerThumbnailShadow,
                    backgroundColor: rgbaFromHex(v2.color, Math.min(20, v2.opacity)),
                  }}
                  className="h-6 w-6 rounded-[4px] mr-2"
                />
                <div
                  style={{ border: "1px solid #E2E2FC" }}
                  className="flex items-center justify-between rounded-[4px]"
                >
                  <Select
                    style={{ width: 116, height: 32, fontSize: 10 }}
                    value={type}
                    IconComponent={DropdownChevronIcon}
                    onChange={(e) => handleSelectChange(e)}
                    disableUnderline
                    className="w-full h-full new-effect-select bg-transparent border-none outline-none"
                    MenuProps={{
                      classes: { list: classes.listPadding, paper: classes.menuPaper },
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                      getContentAnchorEl: null,
                      PaperProps: { style: { marginTop: 4 } },
                    }}
                  >
                    <MenuItem style={{ fontSize: 10 }} value="outShadow" className={classes.menuItem}>
                      Drop shadow
                    </MenuItem>
                    {canHaveInnerShadow && (
                      <MenuItem
                        style={{ fontSize: 10 }}
                        value="inShadow"
                        className={classes.menuItem}
                        disabled={isInnerShadowDisabled}
                      >
                        Inner shadow
                      </MenuItem>
                    )}
                    <MenuItem
                      style={{ fontSize: 10 }}
                      value="layerBlur"
                      className={classes.menuItem}
                      disabled={isCreatedFilterBlur}
                    >
                      Layer blur
                    </MenuItem>
                  </Select>
                </div>
              </div>
            </div>
            <CanvasPannableShadowPopover
              popupState={popupState}
              paperClassName={classes.paper}
              anchorPosition={computeEffectsAnchor() ?? { top: 100, left: 100 }}
            >
              {({ paperContentRef, handleBorderMouseDown, isDragging }) => (
                <div
                  ref={paperContentRef}
                  onMouseDown={handleBorderMouseDown}
                  className="bg-white rounded-[8px] p-4"
                  style={{
                    width: 280,
                    userSelect: "none",
                    fontFamily: "Inter, sans-serif",
                    // Outer wrapper owns the `grab` cursor — but only
                    // the padding strip around the content is bare
                    // here. The inner wrapper below resets cursor
                    // back to `default` so labels / text / icons that
                    // don't set their own cursor don't inherit
                    // `grab`. Interactive controls (buttons, inputs,
                    // selects, sliders) keep their native cursors via
                    // their own styles.
                    cursor: isDragging ? "grabbing" : "grab",
                  }}
                >
                  <div style={{ cursor: "default" }} className="flex flex-col gap-3">
                    {/* Header type select */}
                    <div className="rounded-[4px]" style={{ border: "1px solid #E2E2FC" }}>
                      <Select
                        style={{ width: "100%", height: 24, fontSize: 10, paddingLeft: 8 }}
                        value={type}
                        IconComponent={DropdownChevronIcon}
                        onChange={(e) => handleSelectChange(e)}
                        disableUnderline
                        className="w-full bg-transparent border-none outline-none"
                        MenuProps={{
                          classes: { list: classes.listPadding, paper: classes.menuPaper },
                          anchorOrigin: { vertical: "bottom", horizontal: "left" },
                          transformOrigin: { vertical: "top", horizontal: "left" },
                          getContentAnchorEl: null,
                          PaperProps: { style: { marginTop: 4 } },
                        }}
                      >
                        <MenuItem style={{ fontSize: 10 }} value="outShadow" className={classes.menuItem}>
                          Drop shadow
                        </MenuItem>
                        {canHaveInnerShadow && (
                          <MenuItem
                            style={{ fontSize: 10 }}
                            value="inShadow"
                            className={classes.menuItem}
                            disabled={isInnerShadowDisabled}
                          >
                            Inner shadow
                          </MenuItem>
                        )}
                        <MenuItem
                          style={{ fontSize: 10 }}
                          value="layerBlur"
                          className={classes.menuItem}
                          disabled={isCreatedFilterBlur}
                        >
                          Layer blur
                        </MenuItem>
                      </Select>
                    </div>

                    {/* Preview area (passive — position only changes by dragging the light point below) */}
                    <div
                      ref={previewRef}
                      className="relative rounded-[4px] overflow-hidden"
                      style={{
                        width: PREVIEW_W,
                        height: PREVIEW_H,
                        background: "#F2F2F6",
                        border: "1px solid #E2E2FC",
                      }}
                    >
                      {/* The white rect is now a pure preview surface — the
                      crosshair is always clamped inside its bounds
                      (see `updateLightFromMouse`), so the old "click
                      rect to recover from out-of-bounds crosshair"
                      affordance is dead code. The red axis-snap lines
                      came with the now-removed center/axis magnet. */}
                      <div
                        className="absolute bg-white rounded-[4px]"
                        style={{
                          width: RECT_W,
                          height: RECT_H,
                          top: `calc(50% - ${RECT_H / 2}px)`,
                          left: `calc(50% - ${RECT_W / 2}px)`,
                          boxShadow: previewShadowCss,
                        }}
                      />
                      <div
                        className="absolute"
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: "white",
                          border: "2px solid #E2E2FC",
                          boxShadow: "0px 4px 11px rgba(255,255,255,1)",
                          left: lightLeft,
                          top: lightTop,
                          transform: "translate(-50%, -50%)",
                          cursor: isDraggingLight ? "grabbing" : "grab",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setIsDraggingLight(true);
                          updateShapeMouseDown(selectedShape, editor);
                        }}
                        onMouseUp={() => updateShapeMouseUp(selectedShape, editor)}
                      />

                      {/* Vertical blur slider rail */}
                      <div
                        className="absolute"
                        style={{ right: 14, top: 16, bottom: 16, width: 2, background: "#AEAFC7", opacity: 0.5 }}
                      />
                      <div
                        ref={blurSliderRef}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setIsDraggingBlur(true);
                        }}
                        className="absolute"
                        style={{
                          right: 10,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: "white",
                          border: "1px solid #AEAFC7",
                          cursor: "ns-resize",
                          top: `calc(${(1 - Math.min(1, v2.blur / 50)) * 100}% - 5px)`,
                          transition: isDraggingBlur ? "none" : "top 60ms",
                        }}
                      />
                    </div>

                    {/* Position */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#2E3860]">Position</span>
                      <div className="flex flex-1 gap-2">
                        <NumericField label="X" value={v2.x} onChange={(val) => writeShadowProps({ x: val })} />
                        <NumericField label="Y" value={v2.y} onChange={(val) => writeShadowProps({ y: val })} />
                      </div>
                    </div>

                    {/* Blur */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#2E3860]" style={{ width: 36 }}>
                        Blur
                      </span>
                      <NumericField
                        iconLeft={<BlurIcon />}
                        value={v2.blur}
                        onChange={(val) => writeShadowProps({ blur: Math.max(0, val) })}
                        min={0}
                      />
                    </div>

                    {/* Spread */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#2E3860]" style={{ width: 36 }}>
                        Spread
                      </span>
                      <NumericField
                        iconLeft={<SpreadIcon />}
                        value={v2.spread}
                        onChange={(val) => writeShadowProps({ spread: val })}
                      />
                    </div>

                    {/* Color + opacity (rendered by ColorPickerPopover) */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#2E3860]" style={{ width: 36 }}>
                        Color
                      </span>
                      <div className="flex-1">
                        <ColorPickerPopover
                          colorProp={`${type}Array.${v2.id}.color`}
                          selectedShape={selectedShape}
                          setSelectedShape={setSelectedShape}
                          editor={editor}
                          fullWidth
                          color={v2.color}
                          selectedShapes={[selectedShape]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CanvasPannableShadowPopover>
          </>
        )}
      </PopupState>
    </div>
  );
};

function rgbaFromHex(hex: string, opacityPct: number): string {
  let h = (hex || "#000000").trim();
  if (h.startsWith("#")) h = h.slice(1);
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (h.length === 8) h = h.slice(0, 6);
  if (h.length !== 6) h = "000000";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = Math.max(0, Math.min(1, opacityPct / 100)).toFixed(3);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export default ShadowItem;
